import React from 'react';
import { Shield, FileText, AlertCircle, UserCheck, CreditCard, Ban, Scale } from 'lucide-react';

const TermsOfUse = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-turquoise-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
          <div className="flex items-center mb-8">
            <div className="bg-cyan-100 p-4 rounded-xl mr-4">
              <FileText className="w-8 h-8 text-cyan-600" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-gray-900">Terms of Use</h1>
              <p className="text-gray-600 mt-2">Last updated: January 11, 2025</p>
            </div>
          </div>

          <div className="prose max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <Shield className="w-6 h-6 mr-2 text-cyan-600" />
                1. Acceptance of Terms
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                By accessing and using Fluxara ("the Service"), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to these Terms of Use, please do not use the Service.
              </p>
              <p className="text-gray-700 leading-relaxed">
                These terms constitute a legally binding agreement between you and Fluxara. Your continued use of the Service represents your acceptance of these terms and any modifications.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Service Description</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Fluxara is an AI-powered platform that generates T-shirt designs based on user prompts. The Service includes:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>AI-driven design generation using text prompts</li>
                <li>Background removal and image processing</li>
                <li>Project management and version history</li>
                <li>Design editing and regeneration capabilities</li>
                <li>Download of print-ready PNG files</li>
                <li>Credit-based usage system</li>
                <li>Subscription plans for additional credits</li>
                <li>Cloud storage for your projects</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <UserCheck className="w-6 h-6 mr-2 text-cyan-600" />
                3. User Accounts
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                To use certain features of the Service, you must register for an account. You agree to:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Provide accurate, current, and complete information during registration</li>
                <li>Maintain the security of your password and account credentials</li>
                <li>Notify us immediately of any unauthorized use or security breaches</li>
                <li>Be responsible for all activities that occur under your account</li>
                <li>Not share your account with others</li>
                <li>Be at least 13 years of age to use the Service</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                We reserve the right to suspend or terminate accounts that violate these terms or engage in suspicious activity.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <CreditCard className="w-6 h-6 mr-2 text-cyan-600" />
                4. Credits and Subscriptions
              </h2>
              
              <div className="bg-green-50 p-5 rounded-xl border-l-4 border-green-500 mb-4">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Free Credits</h3>
                <p className="text-gray-700 text-sm">
                  New users receive 100 initial credits upon registration. Free plan users receive 40 monthly credits.
                </p>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">Credit Usage</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>New design generation: 36 credits per generation</li>
                <li>Design regeneration/editing: 19 credits per edit</li>
                <li>Credits are consumed immediately upon generation</li>
                <li>Failed generations may be refunded at our discretion</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">Subscription Plans</h3>
              <p className="text-gray-700 leading-relaxed mb-3">
                Paid subscription plans (Basic, Pro, Elite) provide additional monthly credits. Key terms:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Subscriptions automatically renew unless canceled</li>
                <li>Credits do not roll over to the next month</li>
                <li>Cancellation takes effect at the end of the billing period</li>
                <li>You retain access to subscription benefits until period end</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <Scale className="w-6 h-6 mr-2 text-cyan-600" />
                5. Intellectual Property
              </h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Your Content</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                You retain all rights to the prompts you submit and the designs you generate using our Service. See our License page for detailed usage rights.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">Service Content</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                The Service itself, including its original content, features, functionality, user interface, and underlying technology, is owned by Fluxara and is protected by copyright, trademark, and other intellectual property laws.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">Usage Rights by Plan</h3>
              <div className="bg-cyan-50 p-5 rounded-xl">
                <ul className="text-gray-700 space-y-2">
                  <li><strong>Free, Basic, and Pro Plans:</strong> Personal use only</li>
                  <li><strong>Elite Plan:</strong> Includes commercial license for business use</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <Ban className="w-6 h-6 mr-2 text-red-600" />
                6. Acceptable Use
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                You agree not to use the Service to:
              </p>
              <div className="bg-red-50 p-5 rounded-xl border-l-4 border-red-500">
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li>Generate offensive, illegal, or inappropriate content</li>
                  <li>Violate any applicable local, state, national, or international laws</li>
                  <li>Infringe on intellectual property rights of others</li>
                  <li>Attempt to reverse engineer or hack the Service</li>
                  <li>Upload malicious code, viruses, or harmful content</li>
                  <li>Spam, abuse, or overload the Service</li>
                  <li>Impersonate others or misrepresent your affiliation</li>
                  <li>Collect or harvest user data without consent</li>
                  <li>Use automated tools to access the Service without permission</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Payment and Refunds</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Payment</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Subscription fees are charged via PayPlus payment processor. By providing payment information, you authorize us to charge your payment method for all fees incurred.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">Billing</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mb-4">
                <li>Charges occur at the beginning of each billing period</li>
                <li>You are responsible for all taxes and fees</li>
                <li>Failed payments may result in service suspension</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">Refunds</h3>
              <p className="text-gray-700 leading-relaxed">
                Due to the nature of digital services and immediate credit consumption, we generally do not offer refunds. However, we may consider refund requests on a case-by-case basis for service issues or billing errors.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Cancellation and Termination</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Your Right to Cancel</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                You may cancel your subscription at any time through your Profile settings. Cancellation takes effect at the end of the current billing period. You will retain access to your subscription benefits until that time.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">Our Right to Terminate</h3>
              <p className="text-gray-700 leading-relaxed">
                We reserve the right to suspend or terminate your account if you violate these terms, engage in fraudulent activity, or for any reason at our discretion. In such cases, you forfeit any remaining credits.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Disclaimer of Warranties</h2>
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-5 mb-4 rounded-lg">
                <div className="flex">
                  <AlertCircle className="w-5 h-5 text-yellow-600 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-gray-700 mb-2">
                      The Service is provided "as is" and "as available" without warranties of any kind, either express or implied, including but not limited to:
                    </p>
                    <ul className="text-sm text-gray-700 space-y-1 ml-4">
                      <li>• Merchantability or fitness for a particular purpose</li>
                      <li>• Non-infringement of third-party rights</li>
                      <li>• Uninterrupted, secure, or error-free operation</li>
                      <li>• Accuracy or quality of AI-generated content</li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Limitation of Liability</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                To the maximum extent permitted by law, Fluxara and its affiliates shall not be liable for:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Indirect, incidental, special, consequential, or punitive damages</li>
                <li>Loss of profits, revenue, data, or business opportunities</li>
                <li>Damages arising from your use or inability to use the Service</li>
                <li>Any unauthorized access to or alteration of your content</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                Our total liability shall not exceed the amount you paid us in the 12 months prior to the event giving rise to liability.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Changes to Terms</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We reserve the right to modify these Terms at any time. We will notify users of significant changes via:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Email notification to your registered address</li>
                <li>In-app notification or banner</li>
                <li>Posting the updated Terms on this page</li>
                <li>Updating the "Last updated" date</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                Your continued use of the Service after changes constitutes acceptance of the new Terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Governing Law</h2>
              <p className="text-gray-700 leading-relaxed">
                These Terms shall be governed by and construed in accordance with the laws of the State of California, United States, without regard to its conflict of law provisions.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Contact Information</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you have any questions about these Terms of Use, please contact us:
              </p>
              <div className="bg-gradient-to-br from-cyan-50 to-turquoise-50 p-6 rounded-xl border border-cyan-200">
                <p className="text-gray-700 mb-2"><strong>Email:</strong> support@fluxaraai.com</p>
                <p className="text-gray-700 mb-2"><strong>Legal:</strong> support@fluxaraai.com</p>
                <p className="text-gray-700"><strong>Address:</strong> Fluxara Inc., 123 Design Street, San Francisco, CA 94102</p>
              </div>
            </section>

            <div className="mt-8 p-4 bg-gray-100 rounded-lg text-center">
              <p className="text-sm text-gray-600">
                By using Fluxara, you acknowledge that you have read, understood, and agree to be bound by these Terms of Use.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfUse;